import { Link } from "react-router-dom";
import { Activity, HeartPulse, Zap, Radio, Shield, Phone, ArrowRight } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { FadeUp } from "../components/Reveal";
import { SERVICES, IMAGES, DOCTOR } from "../data/content";
import { useLang } from "../i18n/i18n";

const ICONS = { activity: Activity, "heart-pulse": HeartPulse, zap: Zap, radio: Radio, shield: Shield };

export default function Services() {
  const { t } = useLang();
  return (
    <div data-testid="services-page">
      <PageHero
        index="/ 2"
        kicker={t.services.kicker}
        titleLines={[t.services.pageTitle1, t.services.pageTitle2]}
        subtitle={t.services.pageSub}
      />

      <section className="relative overflow-hidden bg-ink px-6 py-24 md:px-12 md:py-32">
        <div
          className="pointer-events-none absolute right-0 top-0 h-full w-1/2 opacity-10"
          style={{ backgroundImage: `url(${IMAGES.orRoom})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="relative mx-auto max-w-[1500px]">
          <div className="grid auto-rows-fr gap-6 md:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon] || Activity;
              const item = t.services.items[i];
              const featured = s.featured;
              return (
                <FadeUp key={s.id} delay={i * 0.08} className={featured ? "md:col-span-2 lg:col-span-2" : ""}>
                  <div
                    data-testid={`service-card-${s.id}`}
                    className={`group relative flex h-full flex-col justify-between overflow-hidden border border-white/10 bg-ink2 p-8 transition-colors duration-400 hover:border-crimson/60 md:p-10 ${featured ? "lg:p-14" : ""}`}
                  >
                    <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-crimson/0 blur-3xl transition-colors duration-500 group-hover:bg-crimson/20" />
                    <div className="relative">
                      <div className="mb-10 flex items-start justify-between">
                        <span className="flex h-14 w-14 items-center justify-center border border-crimson/40 text-crimson transition-colors duration-400 group-hover:bg-crimson group-hover:text-white">
                          <Icon className="h-6 w-6" />
                        </span>
                        <span className="font-mono text-sm text-white/25">{s.id}</span>
                      </div>
                      <h3 className={`font-serif font-light leading-snug text-white ${featured ? "text-3xl md:text-4xl" : "text-2xl"}`}>{item.title}</h3>
                      <p className={`mt-5 leading-relaxed text-white/55 ${featured ? "max-w-xl text-lg" : "text-sm"}`}>{item.body}</p>
                    </div>
                    <div className="relative mt-10 h-px w-full bg-white/10">
                      <span className="absolute left-0 top-0 h-px w-0 bg-crimson transition-all duration-500 group-hover:w-full" />
                    </div>
                  </div>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 bg-ink2 px-6 py-20 md:px-12">
        <div className="mx-auto flex max-w-[1500px] flex-col items-start justify-between gap-8 md:flex-row md:items-center">
          <h2 className="font-serif text-3xl font-light text-white md:text-5xl">{t.services.ctaTitle}</h2>
          <div className="flex flex-wrap gap-4">
            <a
              href={DOCTOR.phoneHref}
              data-testid="services-call-button"
              className="group flex items-center gap-3 border border-crimson bg-crimson px-8 py-4 text-sm uppercase tracking-[0.15em] text-white transition-colors hover:bg-transparent"
            >
              <Phone className="h-4 w-4" />
              {DOCTOR.phone}
            </a>
            <Link to="/research" className="group flex items-center gap-2 border border-white/20 px-8 py-4 text-sm uppercase tracking-[0.15em] text-white transition-colors hover:border-white/60">
              {t.services.viewResearch}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
